import React from "react";
import { Wrench, ChevronRight } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";

// The exact field list the backend exposes on /api/public/... (routes_public.py
// PUBLIC_KB_FIELDS). Mirrored here so an internal preview can show precisely
// what a wash-street employee gets, and nothing more.
export const PUBLIC_KB_FIELDS = [
  "id", "title", "category_id", "symptoms", "solution_steps", "media",
  "visible_on_qr", "helpful_count", "not_helpful_count",
];

export const toPublicArticle = (a) =>
  Object.fromEntries(PUBLIC_KB_FIELDS.map((k) => [k, a?.[k]]));

/**
 * The public "Bekende oplossingen" block, as rendered on /m/:qrId.
 *
 * This is the real component, used both by the public page and by the internal
 * "Zo ziet de melder het" preview — a preview that drifts from the real thing is
 * worse than none. Leave `onJumpToReport` out and the report shortcut is not
 * rendered, which is how it renders without a live fault to report.
 */
export const KnownSolutions = ({
  solutions = [],
  feedbackGiven = {},
  onHelpful,
  onNotHelpful,
  onJumpToReport,
  defaultOpenId,
  testid = "known-solutions",
}) => {
  if (!solutions.length) return null;
  return (
    <Card className="space-y-3 p-5" data-testid={testid}>
      <div>
        <h2 className="text-body font-semibold text-foreground">Bekende oplossingen</h2>
        <p className="text-data text-muted-foreground">Probeer dit eerst — misschien ben je zo weer op weg.</p>
      </div>
      <Accordion type="single" collapsible className="w-full" defaultValue={defaultOpenId}>
        {solutions.map((a) => (
          <AccordionItem key={a.id} value={a.id} data-testid={`solution-${a.id}`}>
            <AccordionTrigger className="text-left text-body" data-testid={`solution-trigger-${a.id}`}>
              <span className="flex-1">
                {a.title}
                {a.symptoms?.length ? (
                  <span className="mt-0.5 block text-data font-normal text-muted-foreground">{a.symptoms.slice(0, 3).join(" · ")}</span>
                ) : null}
              </span>
            </AccordionTrigger>
            <AccordionContent>
              {a.solution_steps?.length ? (
                <ol className="mb-3 space-y-2" data-testid={`solution-steps-${a.id}`}>
                  {a.solution_steps.map((s, i) => (
                    <li key={i} className="flex gap-2 text-body text-foreground">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary/10 text-data font-semibold text-primary">{i + 1}</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="mb-3 text-body text-muted-foreground">Nog geen stappen beschreven.</p>
              )}
              {feedbackGiven[a.id] === "yes" ? (
                <p className="text-data text-goed-tekst">Fijn dat het hielp!</p>
              ) : feedbackGiven[a.id] === "no" ? (
                <p className="text-data text-muted-foreground">We hebben dit meegenomen in je melding hieronder.</p>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-data text-muted-foreground">Heeft dit geholpen?</span>
                  <Button type="button" size="sm" variant="outline" data-testid={`solution-yes-${a.id}`} onClick={() => onHelpful?.(a)}>Ja</Button>
                  <Button type="button" size="sm" variant="outline" data-testid={`solution-no-${a.id}`} onClick={() => onNotHelpful?.(a)}>Nee</Button>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {onJumpToReport && (
        <Button variant="secondary" className="w-full justify-between" data-testid="jump-to-report" onClick={onJumpToReport}>
          <span className="flex items-center gap-2"><Wrench size={18} /> Het lukt niet — melding maken</span>
          <ChevronRight size={18} />
        </Button>
      )}
    </Card>
  );
};
